import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  Link,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import axios from 'axios';
import {
  ExpandMore as ExpandMoreIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  CalendarToday as CalendarIcon,
  Link as LinkIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { EXTERNAL_LINK_REL, stripTrackingParams } from '../utils/externalUrlUtils';

const CompanyResearchDialog = ({ open, onClose, companyName: initialCompanyName = '' }) => {
  const [companyName, setCompanyName] = useState(initialCompanyName);
  const [loading, setLoading] = useState(false);
  const [researchData, setResearchData] = useState(null);
  const [error, setError] = useState(null);

  // Auto-search function for when dialog opens
  const performAutoSearch = async () => {
    if (!initialCompanyName.trim()) {
      return;
    }

    setLoading(true);
    setError(null);
    setResearchData(null);

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/company-research/research`, {
        companyName: initialCompanyName.trim()
      });

      setResearchData(response.data.data);
    } catch (err) {
      console.error('Company research error:', err);
      setError(err.message || 'Failed to research company. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setCompanyName(initialCompanyName);
      setResearchData(null);
      setError(null);
      
      // Auto-search if company name is provided
      if (initialCompanyName && initialCompanyName.trim()) {
        performAutoSearch();
      }
    }
  }, [open, initialCompanyName]);

  const handleResearch = async () => {
    if (!companyName.trim()) {
      setError('Please enter a company name');
      return;
    }

    setLoading(true);
    setError(null);
    setResearchData(null);

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/company-research/research`, {
        companyName: companyName.trim()
      });

      setResearchData(response.data.data);
    } catch (err) {
      console.error('Company research error:', err);
      setError(err.message || 'Failed to research company. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderTextBlocks = (textBlocks) => {
    if (!textBlocks || textBlocks.length === 0) return null;

    return textBlocks.map((block, index) => {
      switch (block.type) {
        case 'heading':
          return (
            <Typography
              key={index}
              variant={block.level === 3 ? 'h6' : 'h5'}
              sx={{ 
                mt: block.level === 3 ? 2 : 3, 
                mb: 1,
                fontWeight: 'bold',
                color: 'primary.main'
              }}
            >
              {block.text}
            </Typography>
          );

        case 'paragraph':
          return (
            <Typography
              key={index}
              variant="body1"
              sx={{ mb: 2, lineHeight: 1.6 }}
            >
              {block.snippet}
            </Typography>
          );

        case 'list':
          return (
            <Box key={index} sx={{ mb: 2 }}>
              {block.items?.map((item, itemIndex) => (
                <Box
                  key={itemIndex}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    mb: 1,
                    pl: 2
                  }}
                >
                  <Typography sx={{ mr: 1, mt: 0.5 }}>•</Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                    {item.snippet}
                  </Typography>
                </Box>
              ))}
            </Box>
          );

        default:
          return null;
      }
    });
  };

  const renderReferences = (references) => {
    if (!references || references.length === 0) return null;

    return (
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <LinkIcon sx={{ mr: 1 }} />
            References ({references.length})
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <List>
            {references.map((ref, index) => (
              <ListItem key={index} sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                <Link
                  href={stripTrackingParams(ref.link)}
                  target="_blank"
                  rel={EXTERNAL_LINK_REL}
                  sx={{
                    fontWeight: 'bold',
                    color: 'primary.main',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  {ref.title}
                </Link>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {ref.snippet}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <Chip
                    label={ref.source}
                    size="small"
                    variant="outlined"
                    sx={{ mr: 1 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Index: {ref.index}
                  </Typography>
                </Box>
                <Divider sx={{ width: '100%', mt: 1 }} />
              </ListItem>
            ))}
          </List>
        </AccordionDetails>
      </Accordion>
    );
  };

  const extractKeyInfo = (textBlocks) => {
    if (!textBlocks) return null;

    const keyInfo = {
      founded: null,
      founders: null,
      headquarters: null,
      size: null,
      funding: null
    };

    textBlocks.forEach(block => {
      if (block.type === 'list' && block.items) {
        block.items.forEach(item => {
          const text = item.snippet.toLowerCase();
          if (text.includes('founded:') || text.includes('founded in')) {
            keyInfo.founded = item.snippet;
          } else if (text.includes('founders:')) {
            keyInfo.founders = item.snippet;
          } else if (text.includes('headquarters:') || text.includes('hq:')) {
            keyInfo.headquarters = item.snippet;
          } else if (text.includes('company size:') || text.includes('employees:')) {
            keyInfo.size = item.snippet;
          } else if (text.includes('funding:') || text.includes('funded:')) {
            keyInfo.funding = item.snippet;
          }
        });
      }
    });

    return keyInfo;
  };

  const keyInfo = researchData ? extractKeyInfo(researchData.text_blocks) : null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '80vh' }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center',
        gap: 1,
        pb: 1
      }}>
        <BusinessIcon />
        Company Research
      </DialogTitle>
      
      <DialogContent sx={{ pt: 2 }}>
        {/* Company Name Input */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Company Name"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder="Enter company name to research"
            variant="outlined"
            disabled={loading}
            InputProps={{
              endAdornment: (
                <Button
                  variant="contained"
                  onClick={handleResearch}
                  disabled={loading || !companyName.trim()}
                  startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                  sx={{ ml: 1 }}
                >
                  {loading ? 'Researching...' : 'Research'}
                </Button>
              )
            }}
          />
        </Box>

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '200px',
            flexDirection: 'column',
            gap: 2
          }}>
            <CircularProgress size={40} />
            <Typography variant="body1" color="text.secondary">
              Researching {companyName}...
            </Typography>
          </Box>
        )}

        {/* Research Results */}
        {researchData && !loading && (
          <Box>
            {/* Key Information Cards */}
            {keyInfo && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  <BusinessIcon sx={{ mr: 1 }} />
                  Key Information
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {/* Company Foundation Group */}
                  <Box>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
                      Company Foundation
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 2 }}>
                      {keyInfo.founded && (
                        <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <CalendarIcon sx={{ mr: 1, color: 'primary.main' }} />
                            <Typography variant="subtitle2" fontWeight="bold">Founded</Typography>
                          </Box>
                          <Typography variant="body2">{keyInfo.founded}</Typography>
                        </Box>
                      )}
                      {keyInfo.founders && (
                        <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <PeopleIcon sx={{ mr: 1, color: 'primary.main' }} />
                            <Typography variant="subtitle2" fontWeight="bold">Founders</Typography>
                          </Box>
                          <Typography variant="body2">{keyInfo.founders}</Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>

                  {/* Company Details Group */}
                  <Divider />
                  <Box>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
                      Company Details
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 2 }}>
                      {keyInfo.headquarters && (
                        <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <LocationIcon sx={{ mr: 1, color: 'primary.main' }} />
                            <Typography variant="subtitle2" fontWeight="bold">Headquarters</Typography>
                          </Box>
                          <Typography variant="body2">{keyInfo.headquarters}</Typography>
                        </Box>
                      )}
                      {keyInfo.size && (
                        <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <PeopleIcon sx={{ mr: 1, color: 'primary.main' }} />
                            <Typography variant="subtitle2" fontWeight="bold">Company Size</Typography>
                          </Box>
                          <Typography variant="body2">{keyInfo.size}</Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>

                  {/* Financial Information Group */}
                  {keyInfo.funding && (
                    <>
                      <Divider />
                      <Box>
                        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
                          Financial Information
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 2 }}>
                          <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <MoneyIcon sx={{ mr: 1, color: 'primary.main' }} />
                              <Typography variant="subtitle2" fontWeight="bold">Funding</Typography>
                            </Box>
                            <Typography variant="body2">{keyInfo.funding}</Typography>
                          </Box>
                        </Box>
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Detailed Information */}
            <Typography variant="h6" sx={{ mb: 2 }}>
              Detailed Information
            </Typography>
            {renderTextBlocks(researchData.text_blocks)}

            {/* References */}
            {researchData.references && researchData.references.length > 0 && (
              <Box sx={{ mt: 3 }}>
                {renderReferences(researchData.references)}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompanyResearchDialog;
